import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { DateTime } from 'src_common/utils/time/time';
import { FreeSpinTransactionHistoryTabState } from 'src/components/CustomersBar/CustomerTab/FreeSpinTransactionHistoryTab/FreeSpinTransactionHistoryTab.state';
import { Pagination } from 'src/components/Pagination/PaginationNew';
import Loading from 'src/components/Loading';
import { TableTitleCell, TableData, TableHeadRow, TableDataRow } from './TransactionList.style';
import { FrownIcon } from 'lucide-react';
import classNames from 'classnames';

const EmptyTransactionsState = (): JSX.Element => (
    <div className='flex flex-col items-center justify-center gap-3 py-6 text-center'>
        <FrownIcon className='text-foreground/60' />
        <div className='space-y-1'>
            <p className='font-medium text-foreground'>No transactions found</p>
            <p className='text-sm text-foreground/60'>This user has no transactions to display yet.</p>
        </div>
    </div>
);

const TimeoutTransactionsState = (): JSX.Element => (
    <div className='flex flex-col items-center justify-center gap-3 py-6 text-center'>
        <FrownIcon className='text-foreground/60' />
        <div className='space-y-1'>
            <p className='font-medium text-foreground'>Transactions request timeout</p>
            <p className='text-sm text-foreground/60'>Please try to reload the page.</p>
        </div>
    </div>
);

interface TransactionsListPropsType {
    name: string;
    state: FreeSpinTransactionHistoryTabState;
}

export const TransactionsList = observer('TransactionsList', ({ state }: TransactionsListPropsType) => {
    const elements = state.assetFlowsList.map((assetFlow, idx) => {
        const {
            createdAt,
            accountId,
            bonusId,
            bonusType,
            isBonusBalance,
            provider,
            gameId,
            roundId,
            transactionType,
            amount,
        } = assetFlow;
        const amountNegative = amount < 0;

        const el = (
            <TableDataRow
                className={classNames('transaction-list__row')}
                key={idx}
            >
                <TableData data-test='transaction-date'>
                    {DateTime.from(createdAt)?.format('YYYY-MM-DD') ?? ''}
                </TableData>
                <TableData data-test='transaction-id'>{assetFlow.id}</TableData>
                <TableData data-test='account-id'>{accountId}</TableData>
                <TableData data-test='bonus-id'>{bonusId}</TableData>
                <TableData data-test='bonus-type'>{bonusType}</TableData>

                <TableData data-test='is-bonus-balance'>{isBonusBalance ? 'Yes' : 'No'}</TableData>

                <TableData data-test='provider'>{provider}</TableData>
                <TableData data-test='game-id'>{gameId}</TableData>
                <TableData data-test='round-id'>{roundId}</TableData>

                <TableData data-test='transaction-type'>{transactionType}</TableData>

                <TableData
                    amountNegative={amountNegative}
                    data-test='transaction-amount'
                >
                    {amount}
                </TableData>
            </TableDataRow>
        );

        return [el];
    });

    return (
        <div>
            <table>
                <thead>
                    <TableHeadRow>
                        <TableTitleCell>Date</TableTitleCell>
                        <TableTitleCell>Transaction ID</TableTitleCell>
                        <TableTitleCell>Account ID</TableTitleCell>
                        <TableTitleCell>Bonus ID</TableTitleCell>
                        <TableTitleCell>Bonus Type</TableTitleCell>
                        <TableTitleCell>Bonus Balance</TableTitleCell>

                        <TableTitleCell>Provider</TableTitleCell>
                        <TableTitleCell>Game ID</TableTitleCell>
                        <TableTitleCell>Round ID</TableTitleCell>

                        <TableTitleCell>Method</TableTitleCell>
                        <TableTitleCell>Amount</TableTitleCell>
                    </TableHeadRow>
                </thead>
                <tbody>{elements}</tbody>
            </table>

            {state.shouldDisplayEmptyMessage && <EmptyTransactionsState />}

            {state.shouldDisplayTimeoutMessage && <TimeoutTransactionsState />}

            <Pagination
                className='transaction-list__pagination'
                perPage={state.perPage}
                page={state.page}
                total={state.assetFlowsCurrentCount}
                currentVisibleItems={state.assetFlowsListLength}
                onPageChange={state.onPageChange}
            />
            <Loading
                active={state.isAssetFlowsListLoading}
                message='Loading transactions'
            />
        </div>
    );
});
